import {createSlice} from '@reduxjs/toolkit'

import type {ContentHotel, Hotel} from '@findhotel/sapi'
import type {PayloadAction} from '@reduxjs/toolkit'

interface PendingEnable {
  hotelId: string
  component?: string
  trackingContext?: Record<string, unknown>
}

interface ToastData {
  hotel: ContentHotel | Hotel
  checkIn: string
  checkOut: string
  enabled: boolean
  shouldShowNavigationIcon: boolean
  alertsManagementUrl?: string
  saveFramingIsOn?: boolean
}

interface PriceWatchState {
  /**
   * Stores the intent to enable Price Watch after the user finishes an
   * authentication flow (e.g. One-Time-Password) that does not reload the page.
   * Cleared as soon as the Price Watch is successfully created or the process
   * is aborted.
   */
  pendingEnable?: PendingEnable
  /**
   * Stores toast data to be displayed globally, independent of component lifecycle.
   * Used to show price watch feedback when the triggering component is unmounted.
   */
  toast?: ToastData
  /**
   * Stores PriceTrends tracking context when the PriceTrend bottom sheet is open.
   * Used as a fallback in usePriceWatchToggleLogic to enrich price alert events
   * with PriceTrends context, even when a different toggle instance handles the click.
   */
  priceTrendsTrackingContext?: Record<string, unknown>
}

const initialState: PriceWatchState = {}

const {actions, reducer} = createSlice({
  name: 'priceWatch',
  initialState,
  reducers: {
    setPendingEnable(state, action: PayloadAction<PendingEnable>) {
      state.pendingEnable = action.payload
    },
    clearPendingEnable(state) {
      state.pendingEnable = undefined
    },
    showToast(state, action: PayloadAction<ToastData>) {
      state.toast = action.payload
    },
    clearToast(state) {
      state.toast = undefined
    },
    setPriceTrendsTrackingContext(
      state,
      action: PayloadAction<Record<string, unknown>>
    ) {
      state.priceTrendsTrackingContext = action.payload
    },
    clearPriceTrendsTrackingContext(state) {
      state.priceTrendsTrackingContext = undefined
    }
  }
})

export default reducer

export const {
  setPendingEnable,
  clearPendingEnable,
  showToast,
  clearToast,
  setPriceTrendsTrackingContext,
  clearPriceTrendsTrackingContext
} = actions
